import { Reducer, Action, configureStore } from '@reduxjs/toolkit';
import createWebStorage from 'redux-persist/lib/storage/createWebStorage';
import { baseApi } from '@shared/api';
import {
  PersistConfig,
  persistReducer,
  PersistState,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import { setupListeners } from '@reduxjs/toolkit/query';
import { complectationSlice } from '@entities/complectation';
import { pickupSlice } from '@entities/pickup';
import { rootReducer } from './rootReducer';

// fix reference its own type annotation
type RootState = ReturnType<typeof rootReducer>;
declare module 'redux-persist' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  export function persistReducer<S, A extends Action = Action, P = S>(
    config: PersistConfig<S>,
    baseReducer: Reducer<S, A, P>,
  ): Reducer<
    S & { _persist: PersistState },
    A,
    P & { _persist?: PersistState }
  >;
}

const createNoopStorage = () => ({
  getItem() {
    return Promise.resolve(null);
  },
  setItem(_key: string, value: unknown) {
    return Promise.resolve(value);
  },
  removeItem() {
    return Promise.resolve();
  },
});

const storage =
  typeof window === 'undefined'
    ? createNoopStorage()
    : createWebStorage('local');

const persistConfig: PersistConfig<RootState> = {
  key: 'root',
  storage,
  // !!! ACHTUNG
  // When changing the structure of whitelisted slices,
  // you need to CHANGE THE VERSION NUMBER
  version: 1,
  whitelist: [complectationSlice.name, pickupSlice.name],
  migrate: (state, version) => {
    // eslint-disable-next-line no-underscore-dangle
    if (version !== state?._persist.version) return Promise.resolve(undefined);
    return Promise.resolve(state);
  },
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const makeStore = (initialState?: Partial<RootState>) => {
  const store = configureStore({
    preloadedState: initialState || {},
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(baseApi.middleware),
  });
  setupListeners(store.dispatch);
  return store;
};
